import { Formik, Form, Field } from "formik";
import { useState } from "react";
import { technicianSchema } from "../../validation/userSchemas";

import ProInput from "../form/Input";
import MapPickerModal from "../map/MapPickerModal";
import { registerUser } from "../../api/authApi";

export default function AddTechnicianForm({ onClose }) {
  const [mapOpen, setMapOpen] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    setServerError("");
    setSuccessMessage("");
    setLoading(true);

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phone,
      userType: "TECHNICIAN",
      roles: ["FIELD_TECHNICIAN"],
      location: values.location,
    };

    try {
      const result = await registerUser(payload);

      if (result.success) {
        setSuccessMessage("Technician registered successfully.");
        resetForm();
        return;
      }

      setServerError(result.error?.message || "Failed to register technician.");
    } catch (error) {
      setServerError("Something went wrong while registering technician.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-3xl bg-black/40 backdrop-blur-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/70 border-t-transparent" />
          <p className="mt-3 text-sm font-medium text-white">
            Saving technician...
          </p>
        </div>
      )}

      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          location: null,
        }}
        validationSchema={technicianSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => (
          <>
            <Form className="space-y-0">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.35)]">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                      Add Technician
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Register a field technician and assign their base location.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => !loading && onClose?.()}
                    disabled={loading}
                    aria-label="Close"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  >
                    ×
                  </button>
                </div>

                {/* Body */}
                <div className="space-y-6 p-6">
                  {successMessage && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                      {successMessage}
                    </div>
                  )}

                  {serverError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                      {serverError}
                    </div>
                  )}

                  <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                    <div className="mb-5">
                      <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Technician Details
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      <Field
                        name="firstName"
                        label="First Name"
                        icon="👤"
                        component={ProInput}
                      />

                      <Field
                        name="lastName"
                        label="Last Name"
                        icon="👤"
                        component={ProInput}
                      />

                      <Field
                        name="email"
                        label="Email Address"
                        icon="📧"
                        component={ProInput}
                        className="md:col-span-2"
                      />

                      <Field
                        name="phone"
                        label="Phone Number"
                        icon="📱"
                        component={ProInput}
                        className="md:col-span-2"
                      />
                    </div>
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Location
                        </h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Choose the technician’s operating location from the map.
                        </p>
                      </div>

                      {values.location && (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          Selected
                        </span>
                      )}
                    </div>

                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => !loading && setMapOpen(true)}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && !loading) {
                          setMapOpen(true);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      <Field name="location">
                        {({ field, form }) => (
                          <div className="space-y-3">
                            <ProInput
                              field={{
                                name: "location",
                                value: field.value
                                  ? `${field.value.lat}, ${field.value.lng}`
                                  : "",
                              }}
                              form={form}
                              label="Location"
                              icon="📍"
                              readOnly
                              placeholder="Click to select technician location"
                            />

                            <div className="flex items-center justify-between rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600 transition hover:border-slate-500 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500">
                              <span>
                                {field.value
                                  ? "Location selected. Click to update."
                                  : "No location selected yet."}
                              </span>
                              <span className="font-medium text-slate-900 dark:text-white">
                                {field.value ? "Change" : "Open Map"}
                              </span>
                            </div>
                          </div>
                        )}
                      </Field>
                    </div>
                  </section>
                </div>

                {/* Footer */}
                <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => !loading && onClose?.()}
                    disabled={loading}
                    className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black"
                  >
                    {loading ? "Saving..." : "Save Technician"}
                  </button>
                </div>
              </div>
            </Form>

            {mapOpen && !loading && (
              <MapPickerModal
                onClose={() => setMapOpen(false)}
                onSelect={(pos) => {
                  setFieldValue("location", pos);
                  setMapOpen(false);
                }}
              />
            )}
          </>
        )}
      </Formik>
    </div>
  );
}